import type { User } from '@supabase/supabase-js'
import { useEffect } from 'react'
import { create } from 'zustand'
import { config } from '../lib/config'
import type { Profile } from '../lib/database.types'
import { supabase } from '../lib/supabase'

const debug = (...args: unknown[]): void => {
  if (import.meta.env.VITE_DEBUG) console.debug('[authStore]', ...args)
}

interface AuthState {
  user: User | null
  profile: Profile | null
  isLoading: boolean
  isAdmin: boolean
  setUser: (user: User | null) => void
  setProfile: (profile: Profile | null) => void
  setIsLoading: (v: boolean) => void
  signIn: (redirectPath?: string) => Promise<void>
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  profile: null,
  isLoading: true,
  isAdmin: false,
  setUser: (user) => {
    debug('setUser', user?.id ?? null)
    set({ user })
  },
  setProfile: (profile) => {
    debug(
      'setProfile',
      profile?.id ?? null,
      'isAdmin:',
      profile?.role === 'admin',
    )
    set({ profile, isAdmin: profile?.role === 'admin' })
  },
  setIsLoading: (v) => {
    debug('setIsLoading', v)
    set({ isLoading: v })
  },
  signIn: async (redirectPath = '/auth/callback') => {
    debug('signIn called', redirectPath)
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${config.publicUrl}${redirectPath}`,
      },
    })
  },
  signInWithPassword: async (email, password) => {
    debug('signInWithPassword called', email)
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) {
      debug('signInWithPassword error', error.message)
      return { error: error.message }
    }
    debug('signInWithPassword success')
    return { error: null }
  },
  signOut: async () => {
    debug('signOut called')
    await supabase.auth.signOut()
    debug('signOuted')
  },
}))

async function triggerFetchProfile(userId: string, user?: User) {
  const store = useAuthStore.getState()

  try {
    let p = await fetchProfile(userId)
    if (!p && user) {
      p = await upsertProfileFromUser(user)
    }
    store.setProfile(p)
  } finally {
    store.setIsLoading(false)
  }
}

async function upsertProfileFromUser(user: User): Promise<Profile | null> {
  debug('upsertProfileFromUser', user.id)
  const meta = user.user_metadata ?? {}
  const firstName: string | null =
    meta.given_name ?? meta.full_name?.split(' ')[0] ?? null
  const lastName: string | null =
    meta.family_name ?? (meta.full_name?.split(' ').slice(1).join(' ') || null)

  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      { id: user.id, first_name: firstName, last_name: lastName },
      { onConflict: 'id', ignoreDuplicates: true },
    )
    .select('*')
    .single()

  if (error || !data) {
    debug('upsertProfileFromUser error', error?.message ?? 'no data')
    return null
  }

  debug('upsertProfileFromUser success', data.id)
  return data
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  debug('fetchProfile', userId)

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (error || !data) {
    debug('fetchProfile error', error?.message ?? 'no data')
    return null
  }

  debug('fetchProfile success', data.id)
  return data
}

export function useAuthListener(): void {
  useEffect(() => {
    const store = useAuthStore.getState()

    // Each auth event gets its own token. When a newer event fires, it marks
    // the previous token as cancelled so stale profile results are discarded.
    let current = { cancelled: false }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      debug('onAuthStateChange', event, session?.user?.id ?? null)
      current.cancelled = true
      const token = { cancelled: false }
      current = token

      store.setUser(session?.user ?? null)

      if (session?.user) {
        // Note: this func must not await because it will be deadlock via `supabase.auth.onAuthStateChange` and fetch profile.
        triggerFetchProfile(session.user.id, session.user)
      } else {
        store.setProfile(null)
        store.setIsLoading(false)
      }
    })

    return () => {
      debug('Cleaning up auth subscription')
      current.cancelled = true
      subscription.unsubscribe()
      debug('Auth subscription cleaned up')
    }
  }, [])
}
